import {
  CommandBar,
  Icon,
  IconButton,
  Spinner,
  SpinnerSize,
  type ICommandBarItemProps,
} from '@fluentui/react';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { InlineProgress } from '@moko/ui';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  checkWorkReport,
  downloadWorkReport,
  generateWorkReport,
  type IWorkReportInfo,
} from '../../services/workReportService';
import {
  getReportTemplates,
  type IReportTemplate,
} from '../../services/reportTemplateService';
import { formatDate } from '@moko/utils';

interface Props {
  workId: number;
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const WorkReport: FunctionComponent<Props> = ({ workId }) => {
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);

  const [reportInfo, setReportInfo] = useState<IWorkReportInfo | null>(null);
  const [templates, setTemplates] = useState<IReportTemplate[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const cachedBlobRef = useRef<Blob | null>(null);

  const loadPreview = useCallback(
    async (info: IWorkReportInfo) => {
      if (!info.blobUrl) return;
      const abortController = new AbortController();
      const response = await downloadWorkReport(abortController, info.blobUrl);
      const blob = await response.blob();
      cachedBlobRef.current = blob;
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(
        URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      );
      setPageNumber(1);
    },
    [pdfUrl]
  );

  useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        const [reportResult, templateResult] = await Promise.allSettled([
          checkWorkReport(abortController, workId),
          getReportTemplates(abortController, 100, 1),
        ]);

        let existingReport: IWorkReportInfo | null = null;

        if (reportResult.status === 'fulfilled') {
          existingReport = reportResult.value;
          setReportInfo(existingReport);
        }

        if (templateResult.status === 'fulfilled') {
          setTemplates(templateResult.value?.items ?? []);
        } else if (!abortController.signal.aborted) {
          setErrorMessage('Failed to load report templates.');
        }

        if (existingReport) {
          await loadPreview(existingReport);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId, setErrorMessage]);

  const handleGenerate = useCallback(
    async (templateId: number, templateName: string) => {
      setGenerating(true);
      try {
        const abortController = new AbortController();
        const result = await generateWorkReport(
          abortController,
          workId,
          templateId
        );
        setReportInfo(result);
        setSuccessMessage(`Report generated with template "${templateName}".`);
        await loadPreview(result);
      } catch (error: any) {
        setErrorMessage(error?.message ?? 'Failed to generate report.');
      } finally {
        setGenerating(false);
      }
    },
    [workId, setErrorMessage, setSuccessMessage, loadPreview]
  );

  const handleDownload = useCallback(async () => {
    try {
      let blob = cachedBlobRef.current;
      if (!blob) {
        if (!reportInfo?.blobUrl) return;
        const abortController = new AbortController();
        const response = await downloadWorkReport(
          abortController,
          reportInfo.blobUrl
        );
        blob = await response.blob();
        cachedBlobRef.current = blob;
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = reportInfo?.fileName ?? `WorkReport-${workId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to download report.');
    }
  }, [reportInfo, workId, setErrorMessage]);

  const hasReport = !!reportInfo;
  const hasTemplates = templates.length > 0;

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'generate',
      text: 'Generate Report',
      iconProps: { iconName: 'Processing' },
      disabled: !hasTemplates || generating,
      subMenuProps: hasTemplates
        ? {
            items: templates.map((t) => ({
              key: t.id.toString(),
              text: t.fileName,
              onClick: () => {
                handleGenerate(t.id, t.fileName);
              },
            })),
          }
        : undefined,
    },
    {
      key: 'download',
      text: 'Download Report',
      iconProps: { iconName: 'Download' },
      disabled: !hasReport || generating,
      onClick: () => {
        handleDownload();
      },
    },
    {
      key: 'email',
      text: 'Email Report',
      iconProps: { iconName: 'Mail' },
      disabled: !hasReport || generating,
      onClick: () => console.log('Email Report'),
    },
  ];

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}
      >
        <Spinner size={SpinnerSize.large} label="Loading..." />
      </div>
    );
  }

  const previewAreaStyle: React.CSSProperties = {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    paddingBottom: 16,
    minHeight: 120,
    overflow: 'hidden',
    maxWidth: 600,
    margin: '12px auto 0',
  };

  return (
    <>
      {reportInfo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            marginBottom: 4,
            backgroundColor: '#f3f2f1',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <Icon
            iconName="CompletedSolid"
            styles={{ root: { color: '#107c10', fontSize: 16 } }}
          />
          <span>
            Last generated on {formatDate(reportInfo.creationDate, true)}
          </span>
        </div>
      )}
      <CommandBar items={commandBarItems} />

      {reportInfo?.fileName && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            margin: '8px 0 4px',
            fontSize: 14,
            fontWeight: 600,
            color: '#333',
          }}
        >
          <Icon iconName="PDF" styles={{ root: { fontSize: 16 } }} />
          {reportInfo.fileName}
        </div>
      )}

      {generating ? (
        <div style={previewAreaStyle}>
          <InlineProgress label="Generating report..." />
        </div>
      ) : pdfUrl ? (
        <div style={previewAreaStyle}>
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }: { numPages: number }) =>
              setNumPages(numPages)
            }
            onLoadError={(error: Error) =>
              console.error('[react-pdf] Load error:', error)
            }
          >
            <Page pageNumber={pageNumber} width={600} />
          </Document>
          {numPages && numPages > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 8,
              }}
            >
              <IconButton
                iconProps={{ iconName: 'ChevronLeft' }}
                title="Previous page"
                ariaLabel="Previous page"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              />
              <span>
                Page {pageNumber} of {numPages}
              </span>
              <IconButton
                iconProps={{ iconName: 'ChevronRight' }}
                title="Next page"
                ariaLabel="Next page"
                disabled={pageNumber >= numPages}
                onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            ...previewAreaStyle,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#666',
          }}
        >
          {hasTemplates
            ? 'No report generated yet. Select a template from "Generate Report" above.'
            : 'No templates available. Upload one in Report template.'}
        </div>
      )}
    </>
  );
};

export default WorkReport;
